import { DataApi, DataContext } from 'components/common-form/contexts/DataContext';
import { FieldMetadataContext } from 'components/common-form/contexts/FieldMetadataContext';
import { FormOrientationContext } from 'components/common-form/contexts/FormOrientationContext';
import { ProductViewContext } from 'components/common-form/contexts/ProductViewContext';
import { QuotedContext } from 'components/common-form/contexts/QuotedContext';
import { WithInvalidFieldsContext } from 'components/common-form/contexts/WithInvalidFieldsContext';
import type { QuoteKeys, QuoteKeysPrefix } from 'locales/quoteKeys';
import type { FormRecord } from 'models/formError';
import type { HistoricalTrade } from 'models/forms/AddonManagementFormResult';
import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { getView } from 'state/addonManagementForm/filters/filters.selectors';
import { getTableRowBackgroundColor } from 'utils/styling/tableRows';

import { AddonManagementDefaultForm } from './AddonManagementDefaultForm';
import type { AddonManagementFormProps } from './AddonManagementForm.connect';
import { connectAddonManagementForm } from './AddonManagementForm.connect';
import { AddonManagementHistoricForm, AddonManagementPrimaryForm } from './AddonManagementHistoricForm';

export function RawAddonManagementForm({
  formId,
  productDescription,
  isUpfrontFeeColumnHidden,
  isOfferPriceColumnHidden,
  isFixedAmountInPercentColumnHidden,
  isTradingPriceColumnHidden,
  fieldsMetadata,
  inputData,
  historicalTrades,
  pendingEnrichmentData,
  enrichedData,
  resultData,
  isFormSelected,
  status,
  isReadOnly,
  errors,
  warnings,
  validationErrors,
  formJsonRepresentation,
  setFieldValue,
  toggleCheckbox,
  hasTimeoutError,
  hasTechnicalError,
}: AddonManagementFormProps) {
  const data = useMemo(
    () => ({
      ...enrichedData,
      ...pendingEnrichmentData,
      ...inputData,
      ...resultData,
    }),
    [enrichedData, pendingEnrichmentData, inputData, resultData],
  );
  const dataApi: DataApi = useMemo(() => ({ setFieldValue }), [setFieldValue]);

  const view = useSelector(getView);
  const gridColumnSize = productDescription[0]?.children?.filter(x => x.type !== 'Technical').length ?? 12;

  const primaryTrade = useMemo(() => historicalTrades?.find(trade => trade.status === 'Booked'), [historicalTrades]);

  const sortedHistoricalTrades = historicalTrades
    .filter(trade => trade.tradeId !== primaryTrade?.tradeId)
    .sort((a: HistoricalTrade, b: HistoricalTrade) => {
      const bCreatedOn = b.createdOn ? new Date(b.createdOn).getTime() : 0;
      return a.createdOn ? new Date(a.createdOn).getTime() : 0 - bCreatedOn;
    });

  const [isHovered, setIsHovered] = useState(false);
  const onMouseEnter = useCallback(() => setIsHovered(true), []);
  const onMouseLeave = useCallback(() => setIsHovered(false), []);

  const bgColor = useMemo(() => getTableRowBackgroundColor(isFormSelected, isHovered), [isHovered, isFormSelected]);

  return (
    <ProductViewContext.Provider value="AddonManagement">
      <QuotedContext.Provider value={status === 'Quoted'}>
        <FieldMetadataContext.Provider value={fieldsMetadata}>
          <DataContext.Provider value={[data, dataApi]}>
            <WithInvalidFieldsContext<FormRecord, QuoteKeys, QuoteKeysPrefix> validationErrors={validationErrors}>
              <FormOrientationContext.Provider value="grid">
                <AddonManagementDefaultForm
                  formId={formId}
                  productDescription={productDescription}
                  isUpfrontFeeColumnHidden={isUpfrontFeeColumnHidden}
                  isOfferPriceColumnHidden={isOfferPriceColumnHidden}
                  isFixedAmountInPercentColumnHidden={isFixedAmountInPercentColumnHidden}
                  isTradingPriceColumnHidden={isTradingPriceColumnHidden}
                  fieldsMetadata={fieldsMetadata}
                  inputData={inputData}
                  pendingEnrichmentData={pendingEnrichmentData}
                  enrichedData={enrichedData}
                  resultData={resultData}
                  isFormSelected={isFormSelected}
                  status={status}
                  isReadOnly={isReadOnly}
                  errors={errors}
                  warnings={warnings}
                  validationErrors={validationErrors}
                  formJsonRepresentation={formJsonRepresentation}
                  setFieldValue={setFieldValue}
                  toggleCheckbox={toggleCheckbox}
                  hasTimeoutError={hasTimeoutError}
                  hasTechnicalError={hasTechnicalError}
                  onMouseEnter={onMouseEnter}
                  onMouseLeave={onMouseLeave}
                  bgColor={bgColor}
                />

                <>
                  {(view === 'primary' || view === 'historic') && (
                    <AddonManagementPrimaryForm
                      label="primary"
                      trade={primaryTrade}
                      productDescription={productDescription}
                      gridColumnSize={gridColumnSize}
                      isUpfrontFeeColumnHidden={isUpfrontFeeColumnHidden}
                      isOfferPriceColumnHidden={isOfferPriceColumnHidden}
                      isFixedAmountInPercentColumnHidden={isFixedAmountInPercentColumnHidden}
                      isTradingPriceColumnHidden={isTradingPriceColumnHidden}
                      remunerationMode={enrichedData['RemunerationMode']}
                      onMouseEnter={onMouseEnter}
                      onMouseLeave={onMouseLeave}
                      bgColor={bgColor}
                    />
                  )}
                  {view === 'historic' &&
                    sortedHistoricalTrades.map(historicalTrade => (
                      <AddonManagementHistoricForm
                        key={historicalTrade.tradeId}
                        label="historic"
                        trade={historicalTrade}
                        productDescription={productDescription}
                        gridColumnSize={gridColumnSize}
                        isUpfrontFeeColumnHidden={isUpfrontFeeColumnHidden}
                        isOfferPriceColumnHidden={isOfferPriceColumnHidden}
                        isFixedAmountInPercentColumnHidden={isFixedAmountInPercentColumnHidden}
                        isTradingPriceColumnHidden={isTradingPriceColumnHidden}
                        remunerationMode={enrichedData['RemunerationMode']}
                        onMouseEnter={onMouseEnter}
                        onMouseLeave={onMouseLeave}
                        bgColor={bgColor}
                      />
                    ))}
                </>
              </FormOrientationContext.Provider>
            </WithInvalidFieldsContext>
          </DataContext.Provider>
        </FieldMetadataContext.Provider>
      </QuotedContext.Provider>
    </ProductViewContext.Provider>
  );
}

export const AddonManagementForm = connectAddonManagementForm(RawAddonManagementForm);
